import { useMemo, useCallback } from 'react';

import { useReactHookForm } from '../../../../../../common/hooks/useReactHookForm';

import {
  OperationFields,
  OperationStates,
  OperationTypes,
  OperationFileAttachmentIDs,
  OperationTeamNanoID,
  FetchOperationsCacheKey
} from '../../../../../operationsTypes';
import {
  UpdateOperationFormData,
  UpdateOperationFormOperation,
  UpdateOperationFormFields
} from '../../UpdateOperationForm.types';

import { UPDATE_OPERATION_QUERY } from '../../../../../queries/updateOperation.query';

import { useUpdateOperation } from '../../../../../hooks/useUpdateOperation';

import { DropzoneHelperFiles } from '../../../../../../../helpers/dropzone/DropzoneHelper';
import { convertFileToDropzoneFile } from '../../../../../../../helpers/dropzone/DropzoneHelper/utils/convertFileToDropzoneFile';

import { getDueDate } from '../../../../../../../utils/getDueDate';
import { dateFnsConvert } from '../../../../../../../utils/dateFnsConvert';

import { updateOperationFormSchema } from './useUpdateOperationForm.schema';

interface UseUpdateOperationFormOptions {
  teamNanoId?: OperationTeamNanoID;
  operation: UpdateOperationFormOperation;
  cacheKeys?: FetchOperationsCacheKey[];
  onSuccess?: () => void;
}

function useUpdateOperationForm({
  teamNanoId,
  operation,
  cacheKeys,
  onSuccess
}: UseUpdateOperationFormOptions) {
  const operationDropZoneFileAttachmentIds =
    useMemo<OperationFileAttachmentIDs>(() => {
      return operation?.fileAttachments?.map(({ id }) => id) || [];
    }, [operation]);

  const operationDropZoneFileAttachments = useMemo<DropzoneHelperFiles>(
    () => convertFileToDropzoneFile(operation?.fileAttachments),
    [operation]
  );

  const defaultProjectValues = useMemo<UpdateOperationFormData>(
    () => ({
      clientId: operation?.client?.id || null,
      subject: operation?.subject || '',
      body: operation?.body || '',
      date: operation?.date
        ? dateFnsConvert.toDateTimeLocal(operation.date)
        : getDueDate(new Date()),
      completeDatetime: operation?.completeDatetime
        ? dateFnsConvert.toDateTimeLocal(operation.completeDatetime)
        : undefined,
      operationType: operation?.operationType || OperationTypes.TASK,
      state: operation?.state || OperationStates.COMPLETED,
      fileAttachmentIds: operationDropZoneFileAttachmentIds || [],
      createCompanyRecord: !!operation?.teamId
    }),
    [operationDropZoneFileAttachmentIds, operation]
  );

  const {
    control,
    errors,
    handleSubmitReactHookForm,
    register,
    resetForm,
    watch
  } = useReactHookForm<UpdateOperationFormData>({
    defaultValues: defaultProjectValues,
    isModalForm: true,
    schema: updateOperationFormSchema
  });

  const {
    updateOperation,
    updateOperationErrorMessage,
    updateOperationReset,
    updateOperationLoading
  } = useUpdateOperation({
    query: UPDATE_OPERATION_QUERY,
    cacheKeys
  });

  const resetUpdateOperationForm = useCallback<() => void>(() => {
    resetForm(defaultProjectValues);
  }, [resetForm, defaultProjectValues]);

  const watchCreateCompanyRecord = watch(
    UpdateOperationFormFields.CREATE_COMPANY_RECORD
  );

  return {
    validationErrors: {
      subjectValidationError: errors?.subject?.message,
      bodyValidationError: errors?.body?.message,
      dateValidationError: errors?.date?.message,
      completeDatetimeValidationError: errors?.completeDatetime?.message,
      clientIdValidationError: errors?.clientId?.message,
      createCompanyRecordError: errors?.createCompanyRecord?.message
    },
    control,
    resetUpdateOperationForm,
    updateOperationReset,
    updateOperationErrorMessage,
    updateOperationLoading,
    watchCreateCompanyRecord,
    handleUpdateOperation: handleSubmitReactHookForm({
      onSubmit: async (data) =>
        updateOperation({
          uuid: operation?.id,
          ...data,
          teamNanoId: data.createCompanyRecord ? teamNanoId : null,
          clientId: data.createCompanyRecord ? null : data.clientId,
          completeDatetime: data.completeDatetime
            ? dateFnsConvert.toDateTimeWithTimezone(data.completeDatetime)
            : null,
          date: dateFnsConvert.toDateTimeWithTimezone(data.date)
        }).then(() => {
          onSuccess?.();
        }),
      dirtyFieldsOnly: false
    }),

    registerFields: {
      registerBody: register(OperationFields.BODY),
      registerDate: register(OperationFields.DATE),
      registerCompleteDatetime: register(OperationFields.COMPLETE_DATETIME),
      registerSubject: register(OperationFields.SUBJECT),
      registerCreateCompanyRecord: register(
        UpdateOperationFormFields.CREATE_COMPANY_RECORD
      )
    },
    operationDropZoneFileAttachmentIds,
    operationDropZoneFileAttachments
  };
}

export default useUpdateOperationForm;
